import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AppContext } from '../../context/AppContext';
import SafeIcon from '../../common/SafeIcon';

const SITE_URL = 'https://dayclap.com';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric'
  });
};

const CouplesPreview = ({ data, onBack, editMode, editToken, onGoToStep }) => {
  const { addInvitation, editInvitation } = useContext(AppContext);
  const navigate = useNavigate();
  const [createdToken, setCreatedToken] = useState(editMode ? editToken : null);
  const [copied, setCopied] = useState(false);
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    setSending(true);

    const invitePayload = {
      category: 'couples',
      flowType: 'couples',
      recipientName: data.recipientName,
      occasion: data.templateName || 'Love Card',
      template_id: data.templateId,
      message: data.message,
      senderDisplayName: data.senderName,
      musicTrack: data.musicTrack,
      musicTitle: data.musicTitle,
      photoUrl: data.photoUrl,
      eventDate: data.includeDatePlan ? data.eventDate : null,
      eventTime: data.includeDatePlan ? data.eventTime : null,
      eventLocation: data.includeDatePlan ? data.eventLocation : null,
    };

    if (editMode && editToken) {
      const result = await editInvitation(editToken, invitePayload);
      setSending(false);
      if (result?.success) navigate('/invitations');
    } else {
      const token = await addInvitation(invitePayload);
      setSending(false);
      if (token) setCreatedToken(token);
    }
  };

  const shareUrl = createdToken ? `${SITE_URL}/invite/${createdToken}` : '';

  const handleCopy = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `A love card from ${data.senderName} 💕`,
          text: 'Open this envelope to see your special message 💌',
          url: shareUrl
        });
      } catch (e) { /* cancelled */ }
    } else {
      handleCopy();
    }
  };

  if (createdToken && !editMode) {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="p-6 flex flex-col items-center min-h-[70vh]">
        <motion.div
          initial={{ scale: 0 }} animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 200, damping: 15 }}
          className="w-20 h-20 bg-gradient-to-br from-purple-100 to-violet-100 rounded-full flex items-center justify-center mb-5 shadow-lg"
        >
          <SafeIcon name="Check" className="text-purple-500 w-10 h-10" />
        </motion.div>

        <h2 className="text-2xl font-bold text-gray-800 mb-2 text-center">Envelope Sealed! 💕</h2>
        <p className="text-gray-500 mb-6 text-center text-sm">
          Share this love card with <strong>{data.recipientName}</strong>
        </p>

        <div className="w-full bg-gray-50 border border-gray-200 rounded-xl p-4 mb-5">
          <p className="text-xs text-gray-400 font-semibold uppercase mb-2">Love Card Link</p>
          <div className="flex items-center gap-2">
            <div className="flex-1 truncate text-sm text-gray-700 font-mono">{shareUrl}</div>
            <button onClick={handleCopy} className="flex-shrink-0 bg-purple-500 text-white text-xs font-bold px-3 py-2 rounded-lg hover:bg-purple-600">
              {copied ? '✓ Copied!' : 'Copy'}
            </button>
          </div>
        </div>

        <div className="w-full space-y-3">
          <button onClick={handleCopy} className="w-full bg-gradient-to-r from-purple-500 to-violet-500 text-white font-bold py-4 rounded-xl shadow-lg shadow-purple-200 flex items-center justify-center gap-2">
            <SafeIcon name={copied ? 'Check' : 'Copy'} />
            {copied ? 'Link Copied!' : 'Copy Love Card Link'}
          </button>
          <button onClick={handleShare} className="w-full bg-white border border-gray-200 text-gray-700 font-semibold py-4 rounded-xl flex items-center justify-center gap-2 hover:bg-gray-50">
            <SafeIcon name="Share2" /> Share via App
          </button>
          <button onClick={() => navigate('/home')} className="w-full text-gray-500 font-medium py-3 hover:text-purple-500">
            Done — Go Home
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-4">
      {editMode && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 flex items-center gap-2">
          <SafeIcon name="Edit" className="text-amber-500 w-4 h-4" />
          <p className="text-sm text-amber-700 font-medium">Editing your love card</p>
        </div>
      )}

      {/* Love Card Preview */}
      <div className="bg-gradient-to-b from-purple-50 to-white rounded-3xl p-6 shadow-xl border border-purple-100 relative overflow-hidden flex flex-col justify-center text-center">
        <div className="text-4xl mb-3">💕</div>
        <p className="text-xs text-purple-400 font-semibold uppercase tracking-wider mb-2">
          From {data.senderName} → To {data.recipientName}
        </p>

        {data.photoUrl && (
          <img src={data.photoUrl} alt="Memory" className="w-32 h-32 object-cover rounded-2xl mx-auto mb-4 shadow-lg" />
        )}

        <p className="text-lg text-gray-700 italic mb-4 whitespace-pre-wrap leading-relaxed line-clamp-3">"{data.message}"</p>

        {data.musicTitle && (
          <div className="flex items-center justify-center gap-2 text-xs text-gray-400 mb-4">
            <SafeIcon name="Music" className="text-purple-400" /> {data.musicTitle}
          </div>
        )}

        {data.includeDatePlan && data.eventDate && (
          <div className="bg-purple-50 rounded-xl p-3 border border-purple-100 inline-block mx-auto text-left">
            <p className="text-xs text-purple-400 font-semibold uppercase mb-2">Date Plan</p>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <SafeIcon name="Calendar" className="w-3.5 h-3.5 text-purple-400" />
                <span>{formatDate(data.eventDate)}</span>
              </div>
              {data.eventTime && (
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <SafeIcon name="Clock" className="w-3.5 h-3.5 text-purple-400" />
                  <span>{data.eventTime}</span>
                </div>
              )}
              {data.eventLocation && (
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <SafeIcon name="MapPin" className="w-3.5 h-3.5 text-purple-400" />
                  <span>{data.eventLocation}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Edit steps quick-nav */}
      {onGoToStep && (
        <div className="mt-4">
          <p className="text-center text-xs text-gray-400 mb-2">
            👆 Tap any section below to go back and edit it
          </p>
          <div className="flex flex-wrap gap-2 justify-center">
            {[
              { label: 'Names', step: 0 },
              { label: 'Template', step: 1 },
              { label: 'Message', step: 2 },
              { label: 'Photo', step: 3 },
              { label: 'Music', step: 4 },
              { label: 'Date Plan', step: 5 },
            ].map(({ label, step: s }) => (
              <button
                key={s}
                onClick={() => onGoToStep(s)}
                className="text-xs bg-gray-100 text-gray-600 px-3 py-1.5 rounded-full hover:bg-purple-100 hover:text-purple-600 transition-colors font-medium"
              >
                ✏️ {label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 flex gap-4">
        <button onClick={onBack} className="flex-1 bg-white border border-gray-200 text-gray-700 font-bold py-4 rounded-xl">
          ← Back
        </button>
        <button
          onClick={handleSend} disabled={sending}
          className="flex-[2] bg-gradient-to-r from-purple-500 to-violet-500 text-white font-bold py-4 rounded-xl shadow-lg shadow-purple-200 disabled:opacity-60 flex items-center justify-center gap-2"
        >
          {sending ? (
            <><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            {editMode ? 'Updating...' : 'Sealing...'}</>
          ) : (
            <><SafeIcon name="Send" /> {editMode ? 'Update Love Card' : 'Seal & Send'}</>
          )}
        </button>
      </div>
    </motion.div>
  );
};

export default CouplesPreview;